import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import classnames from 'classnames';
import { xingConnect } from '@xingternal/xing-frame-provider';
import Modal, { ModalContainer } from '@xingternal/malt-modal';

import SearchInput from './SearchInput';
import ExtendedSearchDropdown from './ExtendedSearchDropdown';
import SearchSuggestionsTracking from './SearchSuggestions';
import { trackToLocalStorage, trackings } from './tracking';
import { trackingURL } from '@xingternal/xing-view/xing-tracking';
import styles from './SearchBar.css';

class SearchBar extends Component {
  static displayName = 'SearchBar';

  static propTypes = {
    className: PropTypes.string,
    hasActiveSearchBar: PropTypes.bool,
    hasSearchSuggestions: PropTypes.bool,
    searchSuggestions: PropTypes.array,
    activateSearchBar: PropTypes.func,
    activeItem: PropTypes.string,
    deactivateSearchBar: PropTypes.func,
    searchTerm: PropTypes.string,
    triggerSearchInput: PropTypes.func,
    executeSearchTracking: PropTypes.func,
    user: PropTypes.object,
  };

  state = {
    searchInputFocused: false,
  };

  handleSearchInputFocus = () => {
    this.setState({ searchInputFocused: true });
  };

  handleSearchInputBlur = () => {
    this.setState({ searchInputFocused: false });
  };

  searchLink = ({ query, trackingKey }) => {
    const searchSection = [
      'news',
      'members',
      'jobs',
      'events',
      'communities',
      'companies',
    ].includes(this.props.activeItem)
      ? this.props.activeItem
      : 'members';

    const baseParams = `advanced_form=open&hrd=1&keywords=${query}`;

    const path =
      searchSection === 'jobs'
        ? `/jobs/search?paging_context=global_search&${baseParams}`
        : `/search/${searchSection}?${baseParams}`;

    return trackingURL(path, trackingKey)(trackings);
  };

  render() {
    const {
      hasActiveSearchBar,
      hasSearchSuggestions,
      searchSuggestions,
      activateSearchBar,
      deactivateSearchBar,
      searchTerm,
      triggerSearchInput,
      executeSearchTracking,
      user,
    } = this.props;

    return (
      <Downshift
        // override id as auto-generated is differs in server <> client when not reset
        // on each render on server side
        // https://github.com/paypal/downshift#resetidcounter
        id="malt-xing-frame-ui-search"
        isOpen={hasActiveSearchBar}
        itemToString={item => (item ? item.title : '')}
        onSelect={item => {
          if (item) {
            item.trackingData &&
              trackToLocalStorage({
                key: 'recommendation-tracking',
                data: item.trackingData,
                url: item.url,
              });

            item.url && window.location.assign(item.url);
          }
        }}
      >
        {({ getItemProps, getInputProps, highlightedIndex }) => {
          return (
            <div className={classnames(styles.searchBar, this.props.className)}>
              <ModalContainer className={styles.innerSearchBar}>
                <SearchInput
                  getInputProps={getInputProps}
                  className={styles.input}
                  query={searchTerm}
                  isOpened={hasActiveSearchBar}
                  searchLink={this.searchLink}
                  triggerInput={triggerSearchInput}
                  activate={activateSearchBar}
                  deactivate={deactivateSearchBar}
                  highlightedIndex={highlightedIndex}
                  onFocus={this.handleSearchInputFocus}
                  onBlur={this.handleSearchInputBlur}
                />
                {hasSearchSuggestions && hasActiveSearchBar && (
                  <Modal
                    onOutsideClick={e => {
                      // propagation is stopped, because otherwise if you click the clear button
                      // the onOutsideClick callback is called first, causing the search button to be visible again
                      // the click event then reaches the search button instead of the clear button, causing the search to be opened again
                      // tldr: you can't close the search by clicking the clear button without the next line
                      e.stopPropagation();
                      // When the user focuses the search input by clicking into it, the following happens
                      // Firstly, the focus event is emitted. Which causes the search suggesions to be displayed
                      // Secondly, a click event is emitted as well. Since the suggestions are already opened at this point
                      // the <Modal> assums this click to be an onOutsideClick, causing the suggestions to be immediately closed again
                      // We prevent that by only closing the search suggestions when the  onOutsideClick whilst the inout it *not* focused
                      // This is also because we cannot use e.preventDefault or similar in the input's click event handler, because onOutsideClick does not use the react event system
                      !this.state.searchInputFocused && deactivateSearchBar();
                    }}
                    className={styles.suggestions}
                  >
                    <SearchSuggestionsTracking
                      highlightedIndex={highlightedIndex}
                      getItemProps={getItemProps}
                      suggestions={searchSuggestions}
                      userId={user.id}
                      executeSearchTracking={executeSearchTracking}
                      query={searchTerm}
                      searchLink={this.searchLink}
                    />
                  </Modal>
                )}
              </ModalContainer>
              <ExtendedSearchDropdown
                searchTerm={searchTerm}
                searchLink={this.searchLink}
              />
            </div>
          );
        }}
      </Downshift>
    );
  }
}

export { SearchBar };
export default xingConnect()(SearchBar);
